// ── ocrQuota.js — 영수증 OCR 사용량 관리 (V103) ─────────────────────────────
//
// 정책 (구매 전 고지 기준):
//  - 평생 Pro: 총 1,000장 기본 제공 + 월 100장 한도
//  - 소진 시: 충전권(100장, 3,000원 — 래피드 판매, 코드 등록)으로 추가
//  - 무료 체험(3일): 월 20장
//
// 사용량은 users/{uid} 문서에 영구 카운터로 저장 (거래 삭제와 무관):
//   ocrTotalUsed  — 누적 사용량
//   ocrMonthKey   — 'YYYY-MM'
//   ocrMonthUsed  — 해당 월 사용량
//   ocrCredits    — 충전권으로 추가된 장수
//
// (카운트 증가는 클라이언트 트랜잭션 — 베타 단계 트레이드오프. V29 주석과 동일한 이유)

import { useState, useEffect } from 'react';
import { doc, onSnapshot, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../db';

export const OCR_QUOTA = {
  proTotal: 1000,    // 평생 Pro 기본 총량
  proMonthly: 100,   // 월 한도
  trialMonthly: 20,  // 무료 체험 월 한도
  couponSize: 100,   // 충전권 1장당 장수
  couponPrice: 3000, // 충전권 가격 (원)
};

export function monthKey(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

// users doc 데이터 → 쿼터 상태 계산
export function computeQuota(data, { trialActive = false } = {}) {
  const d = data || {};
  const nowKey = monthKey();
  const totalUsed = d.ocrTotalUsed || 0;
  const credits   = d.ocrCredits || 0;
  const monthUsed = d.ocrMonthKey === nowKey ? (d.ocrMonthUsed || 0) : 0;

  const totalAllowance = OCR_QUOTA.proTotal + credits;
  const monthlyCap     = trialActive ? OCR_QUOTA.trialMonthly : OCR_QUOTA.proMonthly;

  const remainingTotal = Math.max(0, totalAllowance - totalUsed);
  const remainingMonth = Math.max(0, monthlyCap - monthUsed);

  return {
    totalUsed, credits, monthUsed,
    totalAllowance, monthlyCap,
    remainingTotal, remainingMonth,
    remaining: Math.min(remainingTotal, remainingMonth),
  };
}

// 실시간 쿼터 훅
export function useOcrQuota(user, { trialActive = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    const unsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      setData(snap.exists() ? snap.data() : {});
      setLoading(false);
    });
    return unsub;
  }, [user]);

  return { loading, ...computeQuota(data, { trialActive }) };
}

// OCR n건 사용 기록 (월 롤오버 안전 처리)
export async function consumeOcr(user, n = 1) {
  if (!user || n <= 0) return;
  const ref = doc(db, 'users', user.uid);
  const nowKey = monthKey();
  try {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      const d = snap.exists() ? snap.data() : {};
      const sameMonth = d.ocrMonthKey === nowKey;
      tx.set(ref, {
        ocrTotalUsed: (d.ocrTotalUsed || 0) + n,
        ocrMonthKey: nowKey,
        ocrMonthUsed: (sameMonth ? (d.ocrMonthUsed || 0) : 0) + n,
        ocrLastUsedAt: serverTimestamp(),
      }, { merge: true });
    });
  } catch (e) {
    // 카운트 실패가 OCR 기능을 막으면 안 됨 — 로그만
    console.warn('OCR 사용량 기록 실패:', e);
  }
}

// 한도 안내 메시지
export function quotaMessage(q, { trialActive = false } = {}) {
  if (q.remainingMonth === 0) {
    return `이번 달 영수증 OCR 한도(${q.monthlyCap}장)를 다 썼어요. 다음 달 1일에 초기화돼요.`;
  }
  if (q.remainingTotal === 0) {
    return `영수증 OCR 제공량(총 ${q.totalAllowance.toLocaleString()}장)을 모두 사용했어요. ` +
           `충전권(${OCR_QUOTA.couponSize}장 ${OCR_QUOTA.couponPrice.toLocaleString()}원)을 등록하면 계속 쓸 수 있어요.`;
  }
  if (trialActive) {
    return `무료 체험 중 OCR ${q.monthUsed}/${q.monthlyCap}장 사용`;
  }
  return `OCR 이번 달 ${q.monthUsed}/${q.monthlyCap}장 · 전체 ${q.totalUsed.toLocaleString()}/${q.totalAllowance.toLocaleString()}장 사용`;
}
